import { fetchUserRecommendations, TeamSearchField } from '@atlaskit/smart-common';
import {
	type EntityATI,
	type ExperimentLayer,
	searchPageQuery,
	type SearchPageQueryVariables,
} from '@atlassian/search-client';

import { type PrimaryProductKey } from '../../../../common/constants/products';

export const fetchPeopleAndTeamsResults = async (
	baseUrl: string | undefined,
	query: string,
	organizationId: string,
	primaryProduct: PrimaryProductKey,
	principalId: string,
	siteId: string,
) => {
	// Override townsquare with watermelon as URS does not support townsquare as a primary product
	const productKey = primaryProduct === 'townsquare' ? 'watermelon' : primaryProduct;

	return await fetchUserRecommendations({
		query,
		context: {
			contextType: 'quick_find',
			siteId,
			productKey,
			principalId,
			organizationId,
		},
		includeUsers: true,
		includeTeams: true,
		includeGroups: false,
		maxNumberOfResults: 2,
		performSearchQueryOnly: false,
		searchQuery: {
			filter:
				'account_status:"active" AND (NOT email_domain:"connect.atlassian.com") AND (NOT account_type:app)',
			teamSearchFields: [TeamSearchField.NAME],
		},
		baseUrl,
	});
};

export const fetchConfluenceEntities = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	experimentId: string | undefined,
	shadowExperimentId: string | undefined,
	experimentLayers: ExperimentLayer[] | undefined,
) => {
	const variables = {
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 7,
		commonFilters: {},
		confluenceFilters: {
			spacesFilter: [],
			contributorsFilter: [],
			ancestorIdsFilter: [],
			range: [],
			owners: [],
			labelsFilter: [],
			titleMatchOnly: true,
		},
		jiraFilters: {
			issueFilter: {
				projectARIs: [],
				assigneeARIs: [],
			},
		},
		thirdPartyFilters: {
			titleMatchOnly: false,
			subtypes: [],
		},
		entities: [
			'ati:cloud:confluence:page',
			'ati:cloud:confluence:blogpost',
			'ati:cloud:confluence:attachment',
			'ati:cloud:confluence:whiteboard',
			'ati:cloud:confluence:database',
		] as EntityATI[],
		sort: [],
		searchSessionId,
		sourceProduct,
		experimentId,
		shadowExperimentId,
		experimentLayers,
	} satisfies SearchPageQueryVariables;

	return searchPageQuery({
		variables,
		aggAbsoluteUrl,
	});
};

export const fetchConfluenceSpaces = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	experimentId: string | undefined,
	shadowExperimentId: string | undefined,
	experimentLayers: ExperimentLayer[] | undefined,
) => {
	const variables = {
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 1,
		commonFilters: {},
		confluenceFilters: {
			spacesFilter: [],
			contributorsFilter: [],
			ancestorIdsFilter: [],
			range: [],
			owners: [],
			labelsFilter: [],
		},
		jiraFilters: {
			issueFilter: {
				projectARIs: [],
				assigneeARIs: [],
			},
		},
		thirdPartyFilters: {
			titleMatchOnly: false,
			subtypes: [],
		},
		entities: ['ati:cloud:confluence:space'] as EntityATI[],
		sort: [],
		searchSessionId,
		sourceProduct,
		experimentId,
		shadowExperimentId,
		experimentLayers,
	} satisfies SearchPageQueryVariables;

	return searchPageQuery({
		variables,
		aggAbsoluteUrl,
	});
};
